import { useCallback, useRef } from 'react';
import { t } from '@lingui/macro';
import { canUseDOM, useDisclosure, useEnhancedEffect } from '@landr/maestro';
import { FiltersParams } from 'types/params';
import { ModifiersPayload } from 'controllers/SearchController/types';

const useSearchBar = ({
    addFilter,
    searchControllerRemoveFilter,
    clearAll,
    clearTextInput,
    resetSelectedSuggestion,
    hasTextInputValue,
    isMobile,
    inputValue,
    predictiveModifiers,
}: {
    addFilter: (modifiers: ModifiersPayload[]) => void;
    searchControllerRemoveFilter: (modifiers: ModifiersPayload[]) => void;
    clearAll: () => void;
    clearTextInput: () => void;
    hasTextInputValue: boolean;
    isMobile: boolean;
    resetSelectedSuggestion: () => void;
    inputValue: string;
    predictiveModifiers?: ModifiersPayload;
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const {
        isOpen: isSuggestionsOpen,
        onOpen: openSuggestions,
        onClose,
    } = useDisclosure();

    const closeSuggestions = useCallback(() => {
        onClose();
        resetSelectedSuggestion();
    }, [resetSelectedSuggestion, onClose]);

    const applySuggestion = useCallback(
        (filters?: ModifiersPayload) => {
            if (filters) {
                addFilter([filters]);
            }

            closeSuggestions();
            inputRef?.current?.blur();
            clearTextInput();
        },
        [addFilter, clearTextInput, closeSuggestions],
    );

    const removeFilter = useCallback(
        (modifiers: ModifiersPayload[]) => {
            searchControllerRemoveFilter(modifiers);
            closeSuggestions();
            inputRef?.current?.blur();
        },
        [searchControllerRemoveFilter, closeSuggestions],
    );

    const handleClearCta = useCallback(() => {
        if (hasTextInputValue) {
            clearTextInput();
        } else {
            clearAll();
        }
    }, [hasTextInputValue, clearTextInput, clearAll]);

    const clearCtaLabel = hasTextInputValue ? t`Clear` : t`Clear all`;

    useEnhancedEffect(() => {
        // The following handler is to get the actual height of the viewport.
        // On iOS, the keyboard will cover the modal when opened.
        // The hidden content won't be scrollable because it's not considered an overflow
        if (!canUseDOM || !window?.visualViewport) return;

        function viewportHandler(event: Event) {
            const viewport = event.target as VisualViewport;

            document.documentElement.style.setProperty(
                '--viewport-height',
                `${Math.floor(viewport.height)}px`,
            );
        }

        window.visualViewport.addEventListener('resize', viewportHandler);
        return () => {
            window?.visualViewport?.removeEventListener(
                'resize',
                viewportHandler,
            );
        };
    });

    const handleFocus = useCallback(() => {
        if (!isSuggestionsOpen) {
            isMobile && inputRef?.current?.blur();
            openSuggestions();
        }
    }, [isSuggestionsOpen, isMobile, openSuggestions]);

    const handleApplyTextSearch = useCallback(() => {
        if (predictiveModifiers) {
            applySuggestion(predictiveModifiers);
        } else if (inputValue !== '') {
            applySuggestion([FiltersParams.textsearch, inputValue]);
        }
    }, [applySuggestion, inputValue, predictiveModifiers]);

    return {
        clearCtaLabel,
        inputRef,
        isSuggestionsOpen,
        applySuggestion,
        closeSuggestions,
        handleClearCta,
        handleFocus,
        removeFilter,
        handleApplyTextSearch,
    };
};

export default useSearchBar;
